'use client';

import { ArrowDown01Icon, ArrowUp01Icon } from '@terra/ui-icons';
import { Box, HStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { MetaDefault } from '../../../components/typography';
import { AgentActivityPill } from './AgentActivityPill';
import {
  parallelAgentStatus,
  type ParallelAgentBatchSummary,
} from './parallel-agent-batch';
import type { ParallelAgentExecution } from './parallel-agent-execution';
import { useParallelAgentBatchCopy } from './useParallelAgentBatchCopy';

const MAX_MEMBER_MARKERS = 6;

export function ParallelAgentsPill({
  members,
  summary,
  isLeaving,
  isExpanded,
  onToggle,
}: {
  members: ParallelAgentExecution[];
  summary: ParallelAgentBatchSummary;
  isLeaving: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations('designSystem.chat.parallelAgents');
  const { headline, issues } = useParallelAgentBatchCopy({
    summary,
    memberCount: members.length,
  });

  return (
    <AgentActivityPill
      status={summary.status}
      label={headline}
      title={isExpanded ? t('hideParallelAgents') : t('showParallelAgents')}
      testId="running-agents-trigger"
      state={summary.isFinished ? 'finished' : 'running'}
      isExpanded={isExpanded}
      isLeaving={isLeaving}
      onClick={onToggle}
      detail={
        issues ? (
          <MetaDefault color="fg.muted" flexShrink={0}>
            {issues}
          </MetaDefault>
        ) : null
      }
      trailing={
        <>
          {members.length > 1 ? (
            <HStack gap="2px" alignItems="center" flexShrink={0} aria-hidden>
              {members.slice(0, MAX_MEMBER_MARKERS).map((member) => {
                const status = parallelAgentStatus(member);

                return (
                  <Box
                    key={member.toolCallId}
                    w="4px"
                    h="4px"
                    borderRadius="full"
                    bg={
                      status === 'error'
                        ? 'fg.error'
                        : status === 'completed'
                          ? 'fg.default'
                          : status === 'aborted'
                            ? 'fg.subtle'
                            : 'fg.muted'
                    }
                    animation={
                      status === 'running'
                        ? 'pulse 1.2s ease-in-out infinite'
                        : undefined
                    }
                    transition="background-color 0.3s ease"
                    _motionReduce={{ animation: 'none' }}
                  />
                );
              })}
            </HStack>
          ) : null}
          <Box color="fg.muted" flexShrink={0} display="flex">
            {isExpanded ? (
              <ArrowDown01Icon size={12} />
            ) : (
              <ArrowUp01Icon size={12} />
            )}
          </Box>
        </>
      }
    />
  );
}
